// FLOW-01 (task 5.4c, D20) — os dados do teste (os params de exemplo, o contexto e o agente) num
// estado só, que o popover "Dados do teste" do painel e a caixa do gatilho leem e escrevem: os
// parâmetros do teste moram no nó do gatilho, e o popover continua como atalho para o mesmo lugar.
// A página cria o estado e o entrega pelo `provide`; quem monta o painel sozinho (os testes dele)
// ganha um próprio.
//
// Revisão de UX (M1): os dados se preenchem por um formulário gerado do `params` do YAML (um campo
// por parâmetro, `CamposDoTeste.vue`) ou pelo JSON, à escolha (`modo`). O formulário escreve no
// MESMO texto JSON, então trocar de modo não perde nada, e a caixa e o popover seguem iguais.
//
// FLOW-10 (task 5.2, G17): o "Contexto do teste", as raízes da conversa que o flow lê. São os campos
// do `contexto_usado` da última compilação, cada um com o exemplo do schema (`x-exemplo` do
// `$defs/contexto`) e editável; `now` começa na hora do navegador, no fuso do agente. Com uma
// conversa de teste escolhida, os campos que vêm da conversa são os dela, pela ação do Rails
// (`contexto_da_conversa_de_teste`). O teste manda tudo em `raizes`, pelos nomes das fórmulas
// (`{"contact.phone": "+55...", "now": "..."}`), e o PowerTools os converte como faz no turno real.
// O `ctx` antigo (as chaves do `context_map`) só aparece na entrada que tem `context_map` (Q1).
//
// FLOW-12 (Task 4.5, G6, G7, R5, T6): os dados do teste são de cada pessoa e ficam no servidor
// (`tool_catalog_flow_pins.dados_do_teste`): o estado nasce do `GET dados_do_teste`, e toda mudança
// se salva sozinha, 1 s depois da última, sem botão de salvar. O toggle "Fixar estes dados" e o
// "Usar estes dados" salvam na hora. `dadosValem` diz se os fixados ainda passam nas regras do
// `params` de agora (um param novo obrigatório, um tipo trocado) e qual é o primeiro campo errado.
import { computed, reactive, ref, watch } from 'vue';
import { lerObjetoJson } from './sessaoDeTeste';
import { canonico } from './pegadasDoFlow';
import {
  conferirParams,
  mensagemDoObrigatorio,
  semOpcionaisVazios,
} from './regrasDoParametro';

export const DADOS_DO_TESTE = Symbol('dadosDoTeste');

// FLOW-11, 2ª rodada do Gate 4: o exemplo do "Tem de ser um JSON entre chaves" sai do próprio flow,
// com o primeiro campo do modelo (os params de exemplo, ou as chaves do contexto), e não de um
// `cpf` de outro flow. Sem campo, as chaves vazias.
export function exemploDoJson(modelo) {
  const [par] = Object.entries(modelo || {});
  if (!par) return '{ }';
  return `{ ${JSON.stringify(par[0])}: ${JSON.stringify(par[1])} }`;
}

const json = valor => JSON.stringify(valor, null, 2);
const ehObjeto = valor =>
  !!valor && typeof valor === 'object' && !Array.isArray(valor);

// -- o contexto da conversa (FLOW-10)

// FLOW-05 e FLOW-10: para que a conversa do teste serve: os anexos (`pedeConversa`, o flow tem
// "Ler anexos"), o contexto (o flow lê a conversa) ou os dois; nulo, o flow não lê a conversa.
export function motivoDaConversa(pedeConversa, leConversa) {
  if (pedeConversa) return leConversa ? 'ambos' : 'anexos';
  return leConversa ? 'contexto' : null;
}

// FLOW-12 (Task 5.1, G13): o pedido do teste a partir dos dados do teste, nos campos de
// `corpoDoDepurar`. O param opcional vazio não vai (`semOpcionaisVazios`: é o que o agente não
// mandaria); a conversa só vai quando o flow a lê; e os dados inteiros vão em `dadosDoTeste`, que o
// Rails salva antes de rodar (R5).
export function pedidoDoTeste(dados, { definicoes = {}, motivo = null } = {}) {
  const conversa = motivo ? dados.conversa : '';
  const raizes = dados.raizesDoTeste();
  return {
    params: semOpcionaisVazios(definicoes, dados.params.valor),
    contexto: dados.contexto.valor,
    agentSlug: String(dados.agente || '').trim() || null,
    ...(conversa ? { conversaDeTeste: conversa } : {}),
    ...(raizes ? { raizes } : {}),
    ...(dados.dadosDoTeste ? { dadosDoTeste: dados.dadosDoTeste } : {}),
  };
}

// FLOW-10 (Gate 4): o teste pede o agente quando o flow chama ferramenta (o catálogo e os segredos são
// os dele) e quando lê os atributos da conversa (vêm só os que o agente declarou, como no turno real).
export function pedeAgenteDoTeste(flowSpec) {
  if (!flowSpec) return false;
  return (
    !!flowSpec.ferramentas?.length ||
    (flowSpec.contexto_usado || []).some(
      campo =>
        campo === 'conversation.attributes' ||
        campo.startsWith('conversation.attributes.')
    )
  );
}

// O padrão do runtime e do PowerTools quando o agente não diz o fuso.
export const FUSO_PADRAO = 'America/Sao_Paulo';
// O mapa de chave livre: o flow lê `conversation.attributes.plano`, e a tabela só tem o mapa.
const ATRIBUTOS = 'conversation.attributes';
// Os campos que a conversa de teste dá (a ação do Rails): os de `contact`, `conversation` e
// `message`, menos o idioma, que é a heurística do runtime.
const DA_CONVERSA = /^(contact|conversation|message)\./;
const SO_DO_RUNTIME = ['message.language'];
export const vemDaConversa = caminho =>
  DA_CONVERSA.test(caminho) && !SO_DO_RUNTIME.includes(caminho);

const definicaoDe = (definicao, caminho) =>
  caminho.split('.').reduce((no, parte) => no?.properties?.[parte], definicao);

// Os campos do painel, na ordem do `contexto_usado`: o caminho, o tipo (`x-cel`), o nome para leigo
// e o exemplo. O atributo da conversa (`conversation.attributes.plano`) é texto, com o exemplo do
// mapa quando ele tem a chave. O que a tabela não conhece fica de fora.
export function camposDoContexto(definicao, usados) {
  if (!ehObjeto(definicao)) return [];
  return (usados || [])
    .map(caminho => {
      const def = definicaoDe(definicao, caminho);
      if (def?.['x-cel']) {
        return {
          caminho,
          tipo: def['x-cel'],
          rotulo: def['x-rotulo'] || caminho,
          exemplo: def['x-exemplo'] ?? null,
        };
      }
      if (!caminho.startsWith(`${ATRIBUTOS}.`)) return null;
      const atributo = caminho.slice(ATRIBUTOS.length + 1);
      const mapa = definicaoDe(definicao, ATRIBUTOS)?.['x-exemplo'];
      return {
        caminho,
        tipo: 'string',
        atributo,
        rotulo: '',
        exemplo: ehObjeto(mapa) && atributo in mapa ? mapa[atributo] : null,
      };
    })
    .filter(Boolean);
}

// A hora em ISO 8601 com o offset do fuso (`2026-10-15T18:30:00-03:00`), como o runtime manda o
// `now_iso`. O fuso que não existe cai no padrão, como no PowerTools.
const DOIS = n => String(n).padStart(2, '0');
function relogioDe(instante, fuso) {
  const formato = new Intl.DateTimeFormat('en-US', {
    timeZone: fuso,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  return Object.fromEntries(
    formato.formatToParts(instante).map(parte => [parte.type, parte.value])
  );
}
export function horaNoFuso(instante, fuso = FUSO_PADRAO) {
  let p;
  try {
    p = relogioDe(instante, fuso || FUSO_PADRAO);
  } catch {
    p = relogioDe(instante, FUSO_PADRAO);
  }
  const parede = Date.UTC(
    p.year,
    p.month - 1,
    p.day,
    p.hour,
    p.minute,
    p.second
  );
  const minutos = Math.round(
    (parede - Math.floor(instante.getTime() / 1000) * 1000) / 60000
  );
  const sinal = minutos < 0 ? '-' : '+';
  const offset = `${sinal}${DOIS(Math.floor(Math.abs(minutos) / 60))}:${DOIS(Math.abs(minutos) % 60)}`;
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}${offset}`;
}

// O texto do campo no tipo do contexto: o vazio é null (o "não tem"); o inteiro e o mapa que não
// convertem ficam como texto, e o aviso ao lado diz o que falta (quem recusa é o servidor, que
// nomeia o campo).
export function valorDoTexto(tipo, texto) {
  if (tipo === 'list(string)') {
    return texto
      .split('\n')
      .map(linha => linha.trim())
      .filter(Boolean);
  }
  if (!texto.trim()) return null;
  if (tipo === 'int')
    return /^-?\d+$/.test(texto.trim()) ? Number(texto) : texto;
  if (tipo.startsWith('map')) {
    try {
      return JSON.parse(texto);
    } catch {
      return texto;
    }
  }
  return texto;
}

export function textoDoValor(tipo, valor) {
  if (valor === null || valor === undefined) return '';
  if (Array.isArray(valor)) return valor.join('\n');
  if (ehObjeto(valor)) return JSON.stringify(valor, null, 2);
  return String(valor);
}

const INSTANTE =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})?$/;
// A chave do aviso do campo (`INTEGER`, `TIME`, `MAP`), ou null quando o valor serve.
export function avisoDoValor(tipo, valor) {
  if (valor === null || valor === undefined) return null;
  if (tipo === 'int') return Number.isInteger(valor) ? null : 'INTEGER';
  if (tipo === 'timestamp') {
    return INSTANTE.test(String(valor)) && !Number.isNaN(Date.parse(valor))
      ? null
      : 'TIME';
  }
  if (tipo.startsWith('map')) {
    return ehObjeto(valor) &&
      Object.values(valor).every(item => typeof item === 'string')
      ? null
      : 'MAP';
  }
  return null;
}

// O valor do campo na conversa de teste (as raízes da ação do Rails): `{tem, valor}`. O atributo
// que a conversa não tem vale null (no run, fica de fora, e o `has()` dele é falso).
function naConversa(raizes, caminho) {
  if (!raizes || !vemDaConversa(caminho)) return { tem: false };
  if (caminho.startsWith(`${ATRIBUTOS}.`)) {
    const mapa = raizes[ATRIBUTOS];
    if (!ehObjeto(mapa)) return { tem: false };
    return {
      tem: true,
      valor: mapa[caminho.slice(ATRIBUTOS.length + 1)] ?? null,
    };
  }
  return caminho in raizes
    ? { tem: true, valor: raizes[caminho] }
    : { tem: false };
}

// `paramsDeExemplo`, `chavesDeContexto` e `definicoes` (o `params` do YAML, que dá o tipo e as
// regras de cada campo) são funções (getters): o que o YAML de agora diz.
//
// FLOW-05 L9 (task 9.3): `buscarConversas` traz as conversas de teste da conta (a página a liga à
// API), de onde a "Conversa do teste" (`ConversaDoTeste.vue`) escolhe; sem ela, só o número.
//
// 16/10: `buscarAgentes` traz os agentes da conta (a mesma lista da tela de agentes), de onde o
// "Agente" (`AgenteDoTeste.vue`) escolhe; sem ela, ou com a lista fora, o slug se digita.
//
// FLOW-10 (task 5.2): `contextoUsado` é o `flow_spec.contexto_usado` da última compilação,
// `definicaoDoContexto` o `$defs/contexto` do schema e `buscarContextoDaConversa(numero, fuso)` a
// ação do Rails que dá as raízes da conversa de teste. `ctxLegado` diz se a entrada tem
// `context_map`: sem ele, o `ctx` antigo não aparece (Q1); quem monta o painel sozinho não sabe da
// entrada e o mostra, como antes. `agora` é o relógio (o teste o fixa) e `esperaDaConversa`, a
// pausa antes de buscar a conversa digitada.
//
// FLOW-12 (Task 4.5): `buscarDados` é o `GET dados_do_teste` e `salvarDados(dados)` o `PUT`, já
// presos à conta e à entrada; sem eles, os dados vivem só na página, como antes. `esperaDoSalvar` é
// a pausa depois da última mudança.
export function criarDadosDoTeste({
  paramsDeExemplo,
  chavesDeContexto,
  definicoes = () => ({}),
  buscarConversas = null,
  buscarAgentes = null,
  contextoUsado = () => [],
  definicaoDoContexto = () => null,
  buscarContextoDaConversa = null,
  ctxLegado = () => true,
  agora = () => new Date(),
  esperaDaConversa = 300,
  buscarDados = null,
  salvarDados = null,
  esperaDoSalvar = 1000,
}) {
  const contextoDeExemplo = () =>
    Object.fromEntries(chavesDeContexto().map(chave => [chave, '']));
  const dados = reactive({
    paramsTexto: json(paramsDeExemplo()),
    // Enquanto ninguém mexeu, os params seguem o `params` do YAML.
    paramsEditados: false,
    contextoTexto: json(contextoDeExemplo()),
    // O slug do agente do teste, que o pedido leva em `agent_slug`, ou '' sem agente.
    agente: '',
    agentes: {
      lista: [],
      carregando: false,
      carregada: false,
      falhou: false,
    },
    // 'formulario' | 'json': o mesmo nos dois lugares que mostram os dados.
    modo: 'formulario',
    // O número da conversa do teste (o que a tela da conversa mostra), ou '' sem conversa.
    conversa: '',
    conversas: {
      lista: [],
      carregando: false,
      carregada: false,
      falhou: false,
    },
    // FLOW-10: o que a pessoa digitou no "Contexto do teste", por campo, e as raízes da conversa
    // de teste que a ação do Rails devolveu.
    raizesEditadas: {},
    daConversa: { numero: '', raizes: null, carregando: false, falhou: false },
    // FLOW-12 (T6): "Fixar estes dados": "Executar flow" e o ▶ usam os dados sem abrir a gaveta.
    fixado: false,
    // Os dados do servidor já chegaram (sem servidor, não há o que esperar). Antes disso nada se
    // salva: o PUT troca o mapa inteiro, e salvaria o exemplo por cima do que a pessoa usou.
    carregados: !buscarDados,
    salvamento: { salvando: false, falhou: false, mensagem: null },
  });
  dados.definicoes = computed(() => definicoes() || {});
  dados.chavesDeContexto = computed(() => chavesDeContexto());
  dados.params = computed(() => lerObjetoJson(dados.paramsTexto));
  dados.contexto = computed(() => lerObjetoJson(dados.contextoTexto));
  dados.exemploDosParams = computed(() => exemploDoJson(paramsDeExemplo()));
  dados.exemploDoContexto = computed(() => exemploDoJson(contextoDeExemplo()));
  dados.editarParams = texto => {
    dados.paramsTexto = texto;
    dados.paramsEditados = true;
  };
  // Um campo do formulário: o valor entra no JSON dos params (`undefined` tira a chave). Com o JSON
  // quebrado não há o que mudar: o formulário manda corrigir no JSON.
  dados.editarParam = (nome, valor) => {
    if (dados.params.erro) return;
    const { [nome]: _, ...resto } = dados.params.valor;
    dados.editarParams(
      json(valor === undefined ? resto : { ...resto, [nome]: valor })
    );
  };
  dados.editarContexto = (chave, valor) => {
    if (dados.contexto.erro) return;
    dados.contextoTexto = json({ ...dados.contexto.valor, [chave]: valor });
  };
  dados.mudarModo = modo => {
    dados.modo = modo;
  };
  // Da lista ou digitado: só os algarismos ficam.
  dados.escolherConversa = numero => {
    dados.conversa = String(numero ?? '').replace(/\D/g, '');
  };
  // A lista relê a cada vez que o campo aparece (uma conversa nova do Playground entra), e a de
  // antes fica na tela enquanto isso.
  dados.carregarConversas = async () => {
    if (!buscarConversas || dados.conversas.carregando) return;
    dados.conversas.carregando = true;
    try {
      const lista = await buscarConversas();
      dados.conversas.lista = Array.isArray(lista) ? lista : [];
      dados.conversas.falhou = false;
    } catch {
      dados.conversas.falhou = !dados.conversas.lista.length;
    } finally {
      dados.conversas.carregando = false;
      dados.conversas.carregada = true;
    }
  };
  // Da lista ou digitado (com a lista fora): o slug.
  dados.escolherAgente = slug => {
    dados.agente = String(slug ?? '');
  };
  dados.listaDeAgentes = !!buscarAgentes;
  // Os agentes da conta, uma vez: o popover e a caixa do gatilho dividem a lista.
  dados.carregarAgentes = async () => {
    const { agentes } = dados;
    if (!buscarAgentes || agentes.carregando || agentes.carregada) return;
    agentes.carregando = true;
    try {
      const lista = await buscarAgentes();
      agentes.lista = Array.isArray(lista) ? lista : [];
      agentes.falhou = false;
    } catch {
      agentes.falhou = true;
    } finally {
      agentes.carregando = false;
      agentes.carregada = true;
    }
  };
  dados.recomecarParams = () => {
    dados.paramsTexto = json(paramsDeExemplo());
    dados.paramsEditados = false;
  };

  // -- o contexto da conversa (FLOW-10, G17)

  dados.ctxLegado = computed(() => !!ctxLegado());
  dados.relogio = ref(agora());
  dados.leConversa = computed(() =>
    (contextoUsado() || []).some(vemDaConversa)
  );
  // O fuso do agente no painel: o que o flow lê em `agent.timezone`, ou o padrão.
  const fusoDoPainel = () => {
    const editado = dados.raizesEditadas['agent.timezone'];
    if (typeof editado === 'string' && editado) return editado;
    return FUSO_PADRAO;
  };
  const resolver = (campo, instante) => {
    if (campo.caminho in dados.raizesEditadas) {
      return { valor: dados.raizesEditadas[campo.caminho], origem: 'editado' };
    }
    const daConversa = naConversa(dados.daConversa.raizes, campo.caminho);
    if (daConversa.tem) return { valor: daConversa.valor, origem: 'conversa' };
    // Só `now` começa no relógio: a hora da mensagem (`message.sent_at`, também timestamp desde o
    // Gate 4) começa no exemplo, como os outros campos.
    if (campo.caminho === 'now') {
      return { valor: horaNoFuso(instante, fusoDoPainel()), origem: 'agora' };
    }
    return { valor: campo.exemplo, origem: 'exemplo' };
  };
  dados.camposDoContexto = computed(() =>
    camposDoContexto(definicaoDoContexto(), contextoUsado()).map(campo => {
      const { valor, origem } = resolver(campo, dados.relogio);
      return {
        ...campo,
        valor,
        origem,
        aviso: avisoDoValor(campo.tipo, valor),
      };
    })
  );
  dados.raizesMexidas = computed(
    () => Object.keys(dados.raizesEditadas).length > 0
  );
  dados.editarRaiz = (caminho, valor) => {
    dados.raizesEditadas = { ...dados.raizesEditadas, [caminho]: valor };
  };
  dados.recomecarRaizes = () => {
    dados.raizesEditadas = {};
    dados.relogio = agora();
  };
  // As `raizes` do corpo do teste, ou null quando o flow não lê a conversa (o corpo sai como antes).
  // A hora que ninguém editou é a de agora, e não a de quando o painel abriu.
  dados.raizesDoTeste = () => {
    const campos = camposDoContexto(definicaoDoContexto(), contextoUsado());
    if (!campos.length) return null;
    const instante = agora();
    return Object.fromEntries(
      campos.map(campo => [campo.caminho, resolver(campo, instante).valor])
    );
  };

  // A conversa de teste escolhida (da lista ou digitada) preenche os campos que vêm dela. A
  // resposta que chega depois de outra escolha não vale; os campos que a conversa dá deixam de ser
  // os editados.
  let pedido = 0;
  dados.carregarContextoDaConversa = async () => {
    pedido += 1;
    const meu = pedido;
    const numero = dados.leConversa ? dados.conversa : '';
    if (!numero || !buscarContextoDaConversa) {
      dados.daConversa = {
        numero: '',
        raizes: null,
        carregando: false,
        falhou: false,
      };
      return;
    }
    dados.daConversa = { ...dados.daConversa, numero, carregando: true };
    // Gate 4: o agente do teste vai junto, quando há: os atributos da conversa vêm só os que ele
    // declarou, como no turno real.
    const agente = dados.agente.trim();
    try {
      const raizes = await buscarContextoDaConversa(
        numero,
        fusoDoPainel(),
        ...(agente ? [agente] : [])
      );
      if (meu !== pedido) return;
      dados.raizesEditadas = Object.fromEntries(
        Object.entries(dados.raizesEditadas).filter(
          ([caminho]) => !naConversa(raizes, caminho).tem
        )
      );
      dados.daConversa = {
        numero,
        raizes: ehObjeto(raizes) ? raizes : null,
        carregando: false,
        falhou: !ehObjeto(raizes),
      };
    } catch {
      if (meu !== pedido) return;
      dados.daConversa = {
        numero,
        raizes: null,
        carregando: false,
        falhou: true,
      };
    }
  };
  let espera = null;
  watch(
    () => [dados.conversa, dados.leConversa, dados.agente.trim()],
    () => {
      clearTimeout(espera);
      espera = setTimeout(dados.carregarContextoDaConversa, esperaDaConversa);
    }
  );

  // -- FLOW-12 (Task 4.5): os dados do teste no servidor

  // O que se salva, e o que o `depurar` leva em `dados_do_teste` (G6): os `params`, o `contexto` (o
  // `ctx` antigo e as raízes que a pessoa editou), a `conversa`, o `agente` e o `fixado`. Com o JSON
  // quebrado, nulo: nada se salva até ele voltar a ler.
  const montarDados = () => {
    if (dados.params.erro || dados.contexto.erro) return null;
    return {
      params: dados.params.valor,
      contexto: { ctx: dados.contexto.valor, raizes: dados.raizesEditadas },
      conversa: dados.conversa,
      agente: dados.agente,
      fixado: dados.fixado,
    };
  };
  dados.dadosDoTeste = computed(montarDados);

  // O que o servidor tem (pelo JSON canônico): a mudança que volta ao que já está lá não salva.
  let noServidor = null;
  let esperaDoPut = null;
  dados.salvarAgora = async () => {
    clearTimeout(esperaDoPut);
    esperaDoPut = null;
    const corpo = montarDados();
    if (!salvarDados || !dados.carregados || !corpo) return false;
    const chave = canonico(corpo);
    if (chave === noServidor) return true;
    noServidor = chave;
    dados.salvamento.salvando = true;
    try {
      await salvarDados(corpo);
      dados.salvamento.falhou = false;
      dados.salvamento.mensagem = null;
      return true;
    } catch (erro) {
      // A próxima mudança tenta de novo; o `depurar` também salva (R5). A frase do Rails (o teto de
      // 64 KB, com o tamanho) vai para a gaveta.
      noServidor = null;
      dados.salvamento.falhou = true;
      const frases = erro?.response?.data?.errors;
      dados.salvamento.mensagem = Array.isArray(frases)
        ? frases.join(' ')
        : null;
      return false;
    } finally {
      dados.salvamento.salvando = false;
    }
  };
  watch(
    () => {
      const corpo = montarDados();
      return corpo && canonico(corpo);
    },
    chave => {
      if (!salvarDados || !dados.carregados || !chave || chave === noServidor) {
        return;
      }
      clearTimeout(esperaDoPut);
      esperaDoPut = setTimeout(dados.salvarAgora, esperaDoSalvar);
    }
  );

  // O estado nasce do servidor (R5): os `params` salvos por cima do exemplo, campo a campo (o
  // salvo, senão o `example`, senão vazio), o contexto, a conversa, o agente e o `fixado`.
  dados.aplicarDados = salvos => {
    const de = ehObjeto(salvos) ? salvos : {};
    if (ehObjeto(de.params)) {
      dados.paramsTexto = json({ ...paramsDeExemplo(), ...de.params });
      dados.paramsEditados = true;
    }
    if (ehObjeto(de.contexto?.ctx)) {
      dados.contextoTexto = json(de.contexto.ctx);
    }
    if (ehObjeto(de.contexto?.raizes)) {
      dados.raizesEditadas = { ...de.contexto.raizes };
    }
    if (typeof de.conversa === 'string') dados.conversa = de.conversa;
    if (typeof de.agente === 'string') dados.agente = de.agente;
    dados.fixado = de.fixado === true;
  };
  dados.carregarDados = async () => {
    if (!buscarDados) return false;
    try {
      const corpo = await buscarDados();
      dados.aplicarDados(corpo?.dados_do_teste);
      const lidos = montarDados();
      noServidor = lidos && canonico(lidos);
      return true;
    } catch {
      return false;
    } finally {
      dados.carregados = true;
    }
  };

  // O toggle "Fixar estes dados" (T6) salva na hora; desligar desafixa.
  dados.fixarDados = ligado => {
    dados.fixado = !!ligado;
    return dados.salvarAgora();
  };
  // "Usar estes dados" (T7, G12): os `params` de uma execução viram os dados do teste, e salvam.
  dados.usarDados = params => {
    dados.paramsTexto = json(ehObjeto(params) ? params : {});
    dados.paramsEditados = true;
    return dados.salvarAgora();
  };

  // G7, T6: os dados de agora ainda passam nas regras do `params` (`schema`, o do YAML)? `{valem:
  // true}`, ou `{valem: false, campo, caminho, mensagem, obrigatorio}` com o primeiro campo errado;
  // `obrigatorio` é o param obrigatório vazio ("`cpf` agora é obrigatório"). Com o JSON quebrado,
  // `{valem: false, campo: null}`. O `fixado` não muda: o toggle continua ligado (T6).
  dados.dadosValem = schema => {
    if (dados.params.erro) {
      return { valem: false, campo: null, caminho: [], mensagem: null };
    }
    const erro = conferirParams(schema, dados.params.valor);
    if (!erro) return { valem: true };
    return {
      valem: false,
      ...erro,
      obrigatorio: erro.mensagem === mensagemDoObrigatorio(erro.campo),
    };
  };

  watch(paramsDeExemplo, valor => {
    if (!dados.paramsEditados) dados.paramsTexto = json(valor);
  });
  watch(chavesDeContexto, () => {
    if (dados.contextoTexto === '{}') {
      dados.contextoTexto = json(contextoDeExemplo());
    }
  });
  return dados;
}
